import React, { useContext, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography
} from "@mui/material";
import { PersonRemoveRounded, WarningAmberRounded } from "@mui/icons-material";
import { corporatePersonnel_delete } from "../../../network/api/corporatePersonnel.api";
import { useNavigate } from "react-router-dom";
import { ErrorContext } from "../../../components/GympinPagesProvider";
import { useSelector } from "react-redux";

const _UserDelete = ({ corporatePersonnel }) => {
    const navigate = useNavigate();
    const error = useContext(ErrorContext);
    const corporate = useSelector(({ corporate }) => corporate.corporate);
    const [openModalDeleteUser, setOpenModalDeleteUser] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    if (!corporatePersonnel?.User || corporatePersonnel?.Role === "ADMIN") {
        return null;
    }

    const userName = corporatePersonnel?.User?.FullName || corporatePersonnel?.User?.PhoneNumber || "این کارمند";

    function deleteUser(e) {
        e.preventDefault();
        if (corporate?.Status === "DEMO" || corporate?.Status === "SECURE_DEMO") {
            error.showError({ message: "حذف کاربر در نسخه نمایشی (Demo) فعال نیست" });
            return;
        }

        setSubmitting(true);
        corporatePersonnel_delete({ Id: corporatePersonnel.Id })
            .then(() => {
                setSubmitting(false);
                setOpenModalDeleteUser(false);
                error.showError({ message: "کارمند با موفقیت از سازمان حذف شد" });
                navigate('/personnel', { replace: true });
            }).catch(e => {
                setSubmitting(false);
                try {
                    error.showError({ message: e.response?.data?.Message || "خطا در حذف کاربر" });
                } catch (f) {
                    error.showError({ message: "خطای نامشخص" });
                }
            });
    }

    function renderModalDeleteUser() {
        return (
            <Dialog
                open={openModalDeleteUser}
                onClose={() => !submitting && setOpenModalDeleteUser(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        p: 1,
                        boxShadow: '0 20px 45px rgba(0,0,0,0.12)'
                    }
                }}
            >
                <Box component="form" onSubmit={deleteUser}>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: 'rgba(239, 68, 68, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'error.main'
                            }}
                        >
                            <WarningAmberRounded />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                            حذف کارمند از سازمان
                        </Typography>
                    </DialogTitle>

                    <DialogContent sx={{ pt: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 1.5 }}>
                            آیا از حذف دسترسی <strong>«{userName}»</strong> از سازمان اطمینان دارید؟
                        </Typography>
                        <Box
                            sx={{
                                p: 1.8,
                                borderRadius: 2.5,
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                border: '1px dashed',
                                borderColor: 'divider'
                            }}
                        >
                            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6, display: 'block' }}>
                                💡 توجه: اعتباراتی که تاکنون به کیف پول این کاربر تخصیص یافته، برای وی محفوظ مانده و تا موعد انقضا قابل استفاده خواهد بود.
                            </Typography>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
                        <Button
                            variant="text"
                            onClick={() => setOpenModalDeleteUser(false)}
                            disabled={submitting}
                            sx={{ borderRadius: 2.5, px: 2, color: 'text.secondary' }}
                        >
                            انصراف
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            type="submit"
                            disabled={submitting}
                            sx={{
                                borderRadius: 2.5,
                                px: 3,
                                fontWeight: 700,
                                boxShadow: '0 4px 14px rgba(239, 68, 68, 0.25)'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : "حذف دسترسی"}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        );
    }

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'rgba(239, 68, 68, 0.2)',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.04)' : 'rgba(239, 68, 68, 0.02)',
                overflow: 'hidden'
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'error.main', mb: 0.5 }}>
                    حذف پرسنل از سازمان
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2, lineHeight: 1.6 }}>
                    با حذف کارمند، دسترسی ایشان به پنل سازمانی قطع می‌شود اما اعتبارات فعلی تا زمان انقضا باقی می‌ماند.
                </Typography>
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<PersonRemoveRounded sx={{ ml: 0.8 }} />}
                    onClick={() => setOpenModalDeleteUser(true)}
                    sx={{ borderRadius: 2.2, fontWeight: 700 }}
                >
                    حذف کارمند از سازمان
                </Button>
            </CardContent>

            {renderModalDeleteUser()}
        </Card>
    );
};

export default _UserDelete;

